import React, { useState, useEffect, useCallback } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { fetchHKTasks, updateHKTask, createHKTask, fetchRooms } from '../../services/api.js';
import { OfflineBadge, useOfflineInvalidation } from '../../components/ui.jsx';

// ─── Constants ──────────────────────────────────────────────────────────────
const TASK_TYPES = [
  { id:'checkout_clean', label:'Checkout Clean', icon:'🧹', color:T.danger },
  { id:'stayover',       label:'Stayover',       icon:'🔄', color:T.teal },
  { id:'turndown',       label:'Turndown',       icon:'🌙', color:T.purple },
  { id:'deep_clean',     label:'Deep Clean',     icon:'✨', color:T.warning },
  { id:'inspection',     label:'Inspection',     icon:'🔍', color:T.navy },
];
const PRIORITIES = [
  { id:'urgent', label:'Urgent', color:T.danger },
  { id:'high',   label:'High',   color:T.warning },
  { id:'normal', label:'Normal', color:T.teal },
  { id:'low',    label:'Low',    color:T.muted },
];
const STATUS_FLOW = {
  pending:     { label:'Pending',     color:T.warning, next:'in_progress', nextLabel:'▶ Start',         icon:'⏳' },
  in_progress: { label:'In Progress', color:T.teal,    next:'completed',   nextLabel:'✓ Complete',      icon:'🔄' },
  completed:   { label:'Completed',   color:T.gold,    next:'inspected',   nextLabel:'🔍 Approve',      icon:'✅' },
  inspected:   { label:'Approved',    color:T.success, next:null,          nextLabel:null,              icon:'⭐' },
};
const VIEWS = ['board','rooms','list'];
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtD = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB',
  { day:'numeric', month:'short', year:'numeric' }) : '—';
const staffName = t => t?.assignedToName || t?.assignedName || '';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const pColor = p => PRIORITIES.find(x=>x.id===p)?.color || T.muted;
const tMeta  = t => TASK_TYPES.find(x=>x.id===t) || { icon:'📋', label:t, color:T.muted };
const sMeta  = s => STATUS_FLOW[s] || { label:s, color:T.muted, icon:'❓' };

// ─── Progress Bar ─────────────────────────────────────────────────────────────
function ProgressBar({ done, total, color }) {
  const pct = total ? Math.round((done/total)*100) : 0;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
      <div style={{ flex:1, height:6, background:`${color}20`, borderRadius:99, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:99, transition:'width .4s ease' }} />
      </div>
      <span style={{ fontSize:10, fontWeight:700, color, minWidth:32 }}>{pct}%</span>
    </div>
  );
}

// ─── Kanban Card ─────────────────────────────────────────────────────────────
function KanbanCard({ task, room, onAdvance, advancing }) {
  const sm = sMeta(task.status);
  const tm = tMeta(task.taskType);
  return (
    <div style={{ background:T.white, border:`1px solid ${T.border}`, borderLeft:`3px solid ${pColor(task.priority)}`, borderRadius:11, padding:14, marginBottom:8, boxShadow:SHADOW.sm, transition:'box-shadow .15s' }}
      onMouseEnter={e=>e.currentTarget.style.boxShadow=SHADOW.md}
      onMouseLeave={e=>e.currentTarget.style.boxShadow=SHADOW.sm}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
          <span style={{ fontSize:16 }}>{tm.icon}</span>
          <div>
            <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>Room {room?.roomNumber||task.roomId?.slice(0,6)}</p>
            <p style={{ fontSize:10, color:T.muted }}>{tm.label}</p>
          </div>
        </div>
        <span style={{ fontSize:9, fontWeight:700, padding:'3px 8px', borderRadius:99, background:`${pColor(task.priority)}18`, color:pColor(task.priority), textTransform:'uppercase', letterSpacing:.5, whiteSpace:'nowrap' }}>
          {task.priority}
        </span>
      </div>

      {staffName(task) && (
        <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:6 }}>
          <div style={{ width:20, height:20, borderRadius:'50%', background:`${T.teal}25`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10 }}>👤</div>
          <span style={{ fontSize:11, color:T.slate }}>{staffName(task)}</span>
        </div>
      )}
      {task.scheduledDate && (
        <p style={{ fontSize:10, color:T.muted, marginBottom:6 }}>📅 {fmtD(task.scheduledDate)}</p>
      )}
      {task.notes && <p style={{ fontSize:10, color:T.muted, fontStyle:'italic', marginBottom:8, lineHeight:1.4 }}>{task.notes}</p>}

      {sm.next && (
        <button onClick={()=>onAdvance(task.id, sm.next)} disabled={advancing===task.id} style={{ width:'100%', padding:'7px 0', borderRadius:8, border:'none', background:sm.color, color:T.white, fontSize:11, fontWeight:700, cursor:advancing===task.id?'not-allowed':'pointer', fontFamily:FONTS.body, opacity:advancing===task.id?.6:1, transition:'opacity .15s' }}>
          {advancing===task.id ? '⏳' : sm.nextLabel}
        </button>
      )}
    </div>
  );
}

// ─── Room Card (Room-centric view) ───────────────────────────────────────────
function RoomCard({ room, tasks, onAdvance, advancing, onAssign }) {
  const STATUS_ORDER = { pending:0, in_progress:1, completed:2, inspected:3 };
  const activeTasks = tasks
    .filter(t=>t.roomId===room.id)
    .sort((a, b) => {
      const sa = STATUS_ORDER[a.status] ?? 9;
      const sb = STATUS_ORDER[b.status] ?? 9;
      if (sa !== sb) return sa - sb;
      return (b.scheduledDate || '').localeCompare(a.scheduledDate || '');
    });
  const urgentTask  = activeTasks.find(t=>t.priority==='urgent')||activeTasks[0];
  const sm = urgentTask ? sMeta(urgentTask.status) : null;
  const tm = urgentTask ? tMeta(urgentTask.taskType) : null;

  return (
    <div style={{ background:T.white, border:`2px solid ${urgentTask ? `${sm?.color}50` : T.border}`, borderRadius:14, padding:16, boxShadow:urgentTask?`0 2px 12px ${sm?.color}20`:SHADOW.sm, transition:'all .2s' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
        <div>
          <p style={{ fontSize:16, fontWeight:800, color:T.navy, fontFamily:FONTS.display }}>Rm {room.roomNumber}</p>
          {room.floor && <p style={{ fontSize:9, color:T.muted, marginTop:1 }}>Floor {room.floor}</p>}
        </div>
        <span style={{ fontSize:10, fontWeight:700, padding:'3px 9px', borderRadius:99, background:`${urgentTask?sm?.color:T.muted}18`, color:urgentTask?sm?.color:T.muted }}>
          {urgentTask ? sm?.icon + ' ' + sm?.label : '✓ Clear'}
        </span>
      </div>

      {activeTasks.length === 0 ? (
        <p style={{ fontSize:11, color:T.muted, textAlign:'center', padding:'8px 0' }}>No tasks</p>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {activeTasks.slice(0,2).map(t => (
            <div key={t.id} style={{ display:'flex', alignItems:'center', gap:7, padding:'6px 8px', background:T.surface, borderRadius:8, justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:13 }}>{tMeta(t.taskType).icon}</span>
                <div>
                  <p style={{ fontSize:11, fontWeight:600, color:T.navy }}>{tMeta(t.taskType).label}</p>
                  {staffName(t) && <p style={{ fontSize:9, color:T.muted }}>→ {staffName(t)}</p>}
                </div>
              </div>
              {sMeta(t.status).next && (
                <button onClick={()=>onAdvance(t.id, sMeta(t.status).next)} disabled={advancing===t.id}
                  style={{ fontSize:9, fontWeight:700, padding:'3px 8px', borderRadius:7, border:'none', background:sMeta(t.status).color, color:T.white, cursor:'pointer', fontFamily:FONTS.body, whiteSpace:'nowrap' }}>
                  {advancing===t.id?'…':sMeta(t.status).nextLabel}
                </button>
              )}
            </div>
          ))}
          {activeTasks.length > 2 && <p style={{ fontSize:10, color:T.muted, textAlign:'center' }}>+{activeTasks.length-2} more</p>}
        </div>
      )}
    </div>
  );
}

// ─── New Task Modal ───────────────────────────────────────────────────────────
function NewTaskModal({ rooms, pid, onClose, onCreated }) {
  const [data, setData] = useState({ roomId:'', taskType:'stayover', priority:'normal', assignedName:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = k => v => setData(d => ({ ...d, [k]:v }));
  const sel = (extra={}) => ({ width:'100%', border:`1px solid ${T.border}`, borderRadius:9, padding:'9px 12px', fontSize:13, fontFamily:FONTS.body, outline:'none', background:T.white, ...extra });

  const handle = async () => {
    if (!data.roomId) { setErr('Select a room'); return; }
    setSaving(true); setErr('');
    try { await createHKTask({ ...data, propertyId:pid }); onCreated(); }
    catch(e) { setErr(e.message); setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(13,27,42,.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:T.white, borderRadius:18, width:'100%', maxWidth:520, boxShadow:SHADOW.xl, animation:'scaleIn .2s ease', overflow:'hidden' }}>
        <div style={{ padding:'18px 22px', borderBottom:`1px solid ${T.border}`, background:`linear-gradient(135deg, ${T.teal}08, ${T.navy}05)`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontFamily:FONTS.display, fontSize:20, fontWeight:700, color:T.navy }}>New Housekeeping Task</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:T.muted, lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:22, display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={{ display:'block', fontSize:10, fontWeight:700, color:T.muted, letterSpacing:.8, textTransform:'uppercase', marginBottom:5 }}>Room <span style={{ color:T.danger }}>*</span></label>
            <select value={data.roomId} onChange={e=>set('roomId')(e.target.value)} style={sel()}>
              <option value="">Select room…</option>
              {rooms.map(r=><option key={r.id} value={r.id}>Room {r.roomNumber}{r.floor?` · Floor ${r.floor}`:''}</option>)}
            </select>
          </div>

          <div>
            <label style={{ display:'block', fontSize:10, fontWeight:700, color:T.muted, letterSpacing:.8, textTransform:'uppercase', marginBottom:7 }}>Task Type</label>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:5 }}>
              {TASK_TYPES.map(t=>(
                <button key={t.id} type="button" onClick={()=>set('taskType')(t.id)} style={{ padding:'8px 4px', borderRadius:9, border:`2px solid ${data.taskType===t.id?t.color:T.border}`, background:data.taskType===t.id?`${t.color}15`:T.surface, cursor:'pointer', fontFamily:FONTS.body, transition:'all .15s', textAlign:'center' }}>
                  <div style={{ fontSize:16 }}>{t.icon}</div>
                  <div style={{ fontSize:9, fontWeight:700, color:data.taskType===t.id?t.color:T.muted, marginTop:3, lineHeight:1.2 }}>{t.label.split(' ')[0]}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={{ display:'block', fontSize:10, fontWeight:700, color:T.muted, letterSpacing:.8, textTransform:'uppercase', marginBottom:7 }}>Priority</label>
            <div style={{ display:'flex', gap:6 }}>
              {PRIORITIES.map(p=>(
                <button key={p.id} type="button" onClick={()=>set('priority')(p.id)} style={{ flex:1, padding:'8px', borderRadius:9, border:`2px solid ${data.priority===p.id?p.color:T.border}`, background:data.priority===p.id?`${p.color}15`:T.surface, color:data.priority===p.id?p.color:T.muted, fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:FONTS.body }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={{ display:'block', fontSize:10, fontWeight:700, color:T.muted, letterSpacing:.8, textTransform:'uppercase', marginBottom:5 }}>Assign To</label>
              <input value={data.assignedName} onChange={e=>set('assignedName')(e.target.value)} placeholder="Staff name" style={sel()} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:10, fontWeight:700, color:T.muted, letterSpacing:.8, textTransform:'uppercase', marginBottom:5 }}>Notes</label>
              <input value={data.notes} onChange={e=>set('notes')(e.target.value)} placeholder="Instructions…" style={sel()} />
            </div>
          </div>

          {err && <p style={{ fontSize:11, color:T.danger, padding:'8px 12px', background:T.dangerPale, borderRadius:8 }}>{err}</p>}

          <div style={{ display:'flex', gap:8, paddingTop:4 }}>
            <button onClick={onClose} style={{ flex:1, padding:'10px', borderRadius:10, border:`1px solid ${T.border}`, background:T.surface, color:T.muted, fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:FONTS.body }}>Cancel</button>
            <button onClick={handle} disabled={saving} style={{ flex:2, padding:'10px', borderRadius:10, border:'none', background:T.teal, color:T.white, fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer', fontFamily:FONTS.body, opacity:saving?.6:1 }}>
              {saving ? '⏳ Creating…' : '+ Create Task'}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes scaleIn{from{transform:scale(.96);opacity:0}to{transform:scale(1);opacity:1}}`}</style>
    </div>
  );
}

// ─── Main Housekeeping Board ──────────────────────────────────────────────────
export default function HousekeepingBoard({ property }) {
  const [tasks,     setTasks]     = useState([]);
  const [rooms,     setRooms]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [toast,     setToast]     = useState('');
  const [showNew,   setShowNew]   = useState(false);
  const [advancing, setAdvancing] = useState(null);
  const [view,      setView]      = useState('board');
  const [filterPri, setFilterPri] = useState('');
  const [filterType,setFilterType]= useState('');
  const [filterStaff,setFilterStaff] = useState('');
  const [filterDate,  setFilterDate]  = useState('all'); // all | today
  const pid = property?.id;

  const offlineTick = useOfflineInvalidation('housekeeping');

  const load = useCallback(() => {
    if (!pid) return;
    setLoading(true);
    const dateArg = filterDate === 'today' ? todayStr() : null;
    Promise.all([fetchHKTasks(pid, dateArg), fetchRooms(pid)])
      .then(([t,r]) => { setTasks(Array.isArray(t)?t:[]); setRooms(Array.isArray(r)?r:[]); })
      .catch(() => { setTasks([]); setRooms([]); })
      .finally(() => setLoading(false));
  }, [pid, filterDate]);

  useEffect(() => { load(); }, [load, offlineTick]);

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''), 3000); };

  const handleAdvance = async (id, nextStatus) => {
    setAdvancing(id);
    try { await updateHKTask(id, nextStatus); showToast(`✅ Task → ${sMeta(nextStatus).label}`); load(); }
    catch(e) { showToast(`❌ ${e.message}`); }
    finally { setAdvancing(null); }
  };

  // Stats
  const total      = tasks.length;
  const done       = tasks.filter(t=>t.status==='inspected').length;
  const inProg     = tasks.filter(t=>t.status==='in_progress').length;
  const urgent     = tasks.filter(t=>t.priority==='urgent').length;

  // Filtered tasks
  const filtered = tasks.filter(t =>
    (!filterPri  || t.priority   === filterPri) &&
    (!filterType || t.taskType   === filterType) &&
    (!filterStaff || staffName(t).toLowerCase().includes(filterStaff.toLowerCase()))
  );

  const grouped = { pending:[], in_progress:[], completed:[], inspected:[] };
  filtered.forEach(t => { if (grouped[t.status]) grouped[t.status].push(t); });

  const staffList = [...new Set(tasks.map(staffName).filter(Boolean))];

  const COLS = [
    { key:'pending',     label:'Pending',     color:T.warning, icon:'⏳' },
    { key:'in_progress', label:'In Progress', color:T.teal,    icon:'🔄' },
    { key:'completed',   label:'Completed',   color:T.gold,    icon:'✅' },
    { key:'inspected',   label:'Approved',    color:T.success, icon:'⭐' },
  ];

  return (
    <div className="fade-up">
      <style>{`@keyframes slideUp{from{transform:translateY(10px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', top:70, right:24, zIndex:999, background:toast.startsWith('❌')?T.danger:T.navy, color:T.white, borderRadius:10, padding:'11px 18px', fontSize:13, fontWeight:600, boxShadow:SHADOW.lg, animation:'slideUp .2s ease' }}>
          {toast}
        </div>
      )}

      {/* New Task Modal */}
      {showNew && (
        <NewTaskModal rooms={rooms} pid={pid} onClose={()=>setShowNew(false)} onCreated={()=>{ setShowNew(false); load(); showToast('✅ Task created'); }} />
      )}

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontFamily:FONTS.display, fontSize:'clamp(20px,4vw,28px)', fontWeight:700, color:T.navy }}>Housekeeping</h1>
          <p style={{ fontSize:12, color:T.muted, marginTop:2 }}>
            {total} tasks{filterDate === 'today' ? ' today' : ' (all dates)'} · {inProg} in progress · {done} done
            {urgent > 0 && <span style={{ color:T.danger, fontWeight:700 }}> · {urgent} urgent</span>}
          </p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button onClick={load} style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:9, padding:'9px 14px', fontSize:12, cursor:'pointer', fontFamily:FONTS.body, color:T.slate }}>↻ Refresh</button>
          <button onClick={()=>setShowNew(true)} style={{ background:T.teal, border:'none', borderRadius:9, padding:'9px 18px', fontSize:13, fontWeight:700, color:T.white, cursor:'pointer', fontFamily:FONTS.body }}>+ New Task</button>
        </div>
      </div>

      {/* Progress Overview */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:10, marginBottom:18 }}>
        {COLS.map(col => {
          const cnt = tasks.filter(t=>t.status===col.key).length;
          return (
            <div key={col.key} style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:12, padding:'14px 16px', boxShadow:SHADOW.sm }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:15 }}>{col.icon}</span>
                  <p style={{ fontSize:11, fontWeight:700, color:T.navy }}>{col.label}</p>
                </div>
                <span style={{ fontSize:18, fontWeight:800, color:col.color, fontFamily:FONTS.display }}>{cnt}</span>
              </div>
              <ProgressBar done={cnt} total={total||1} color={col.color} />
            </div>
          );
        })}
      </div>

      {/* Filters + View toggle */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <select value={filterDate} onChange={e=>setFilterDate(e.target.value)} style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 10px', fontSize:12, fontFamily:FONTS.body, outline:'none', color:T.slate, background:T.white, fontWeight: filterDate === 'all' ? 700 : 400 }}>
            <option value="all">All dates</option>
            <option value="today">Today only</option>
          </select>
          <select value={filterPri} onChange={e=>setFilterPri(e.target.value)} style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 10px', fontSize:12, fontFamily:FONTS.body, outline:'none', color:T.slate, background:T.white }}>
            <option value="">All Priorities</option>
            {PRIORITIES.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 10px', fontSize:12, fontFamily:FONTS.body, outline:'none', color:T.slate, background:T.white }}>
            <option value="">All Task Types</option>
            {TASK_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          {staffList.length > 0 && (
            <select value={filterStaff} onChange={e=>setFilterStaff(e.target.value)} style={{ border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 10px', fontSize:12, fontFamily:FONTS.body, outline:'none', color:T.slate, background:T.white }}>
              <option value="">All Staff</option>
              {staffList.map(s=><option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {(filterPri||filterType||filterStaff) && (
            <button onClick={()=>{setFilterPri('');setFilterType('');setFilterStaff('');}} style={{ background:T.dangerPale, border:`1px solid ${T.danger}40`, borderRadius:8, padding:'7px 10px', fontSize:11, color:T.danger, cursor:'pointer', fontFamily:FONTS.body, fontWeight:700 }}>✕ Clear</button>
          )}
        </div>
        <div style={{ display:'flex', background:T.surface, borderRadius:9, padding:3, gap:2, border:`1px solid ${T.border}` }}>
          {[{id:'board',icon:'⊞ Board'},{id:'rooms',icon:'🏨 Rooms'},{id:'list',icon:'☰ List'}].map(v=>(
            <button key={v.id} onClick={()=>setView(v.id)} style={{ padding:'6px 12px', borderRadius:7, border:'none', background:view===v.id?T.white:'transparent', color:view===v.id?T.navy:T.muted, fontSize:11, fontWeight:view===v.id?700:400, cursor:'pointer', fontFamily:FONTS.body, boxShadow:view===v.id?SHADOW.sm:'none', transition:'all .15s' }}>
              {v.icon}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
          {[1,2,3,4].map(i=><div key={i} style={{ height:300, borderRadius:14, background:T.border }} className="shimmer" />)}
        </div>
      ) : view === 'board' ? (
        // ── Kanban Board ──
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14 }}>
          {COLS.map(col => (
            <div key={col.key}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, padding:'8px 12px', background:T.white, borderRadius:10, border:`1px solid ${T.border}`, boxShadow:SHADOW.sm }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:col.color }} />
                <p style={{ fontSize:12, fontWeight:700, color:T.navy, flex:1 }}>{col.label}</p>
                <span style={{ fontSize:12, fontWeight:700, color:col.color, background:`${col.color}15`, padding:'2px 8px', borderRadius:99 }}>{grouped[col.key].length}</span>
              </div>
              {grouped[col.key].length === 0 ? (
                <div style={{ background:T.surface, border:`2px dashed ${T.border}`, borderRadius:12, padding:20, textAlign:'center', color:T.muted, fontSize:11 }}>
                  <p style={{ fontSize:20, marginBottom:6 }}>{col.icon}</p>
                  No tasks
                </div>
              ) : grouped[col.key].map(t => (
                <KanbanCard key={t.id} task={t} room={rooms.find(r=>r.id===t.roomId)} onAdvance={handleAdvance} advancing={advancing} />
              ))}
            </div>
          ))}
        </div>
      ) : view === 'rooms' ? (
        // ── Room Grid View ──
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:12 }}>
          {rooms.map(room => (
            <RoomCard key={room.id} room={room} tasks={filtered} onAdvance={handleAdvance} advancing={advancing} />
          ))}
        </div>
      ) : (
        // ── List View ──
        <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:14, overflow:'hidden', boxShadow:SHADOW.sm }}>
          {filtered.length === 0 ? (
            <p style={{ textAlign:'center', padding:32, color:T.muted, fontSize:13 }}>No tasks match your filters</p>
          ) : filtered.map((t,i) => {
            const room = rooms.find(r=>r.id===t.roomId);
            const sm   = sMeta(t.status);
            const tm   = tMeta(t.taskType);
            return (
              <div key={t.id} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 18px', borderBottom:i<filtered.length-1?`1px solid ${T.border}`:'none' }}>
                <div style={{ width:36, height:36, borderRadius:10, background:`${tm.color}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>{tm.icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <p style={{ fontSize:13, fontWeight:700, color:T.navy }}>Room {room?.roomNumber||t.roomId?.slice(0,6)}</p>
                    <span style={{ fontSize:9, fontWeight:700, padding:'2px 7px', borderRadius:99, background:`${pColor(t.priority)}15`, color:pColor(t.priority), textTransform:'uppercase' }}>{t.priority}</span>
                    <span style={{ fontSize:10, padding:'2px 7px', borderRadius:99, background:`${sm.color}15`, color:sm.color, fontWeight:600 }}>{sm.icon} {sm.label}</span>
                  </div>
                  <p style={{ fontSize:11, color:T.muted }}>
                    {tm.label}
                    {t.scheduledDate ? ` · ${fmtD(t.scheduledDate)}` : ''}
                    {staffName(t) ? ` · 👤 ${staffName(t)}` : ''}
                    {t.notes ? ` · ${t.notes}` : ''}
                  </p>
                </div>
                {sm.next && (
                  <button onClick={()=>handleAdvance(t.id, sm.next)} disabled={advancing===t.id} style={{ padding:'6px 14px', borderRadius:8, border:'none', background:sm.color, color:T.white, fontSize:11, fontWeight:700, cursor:advancing===t.id?'not-allowed':'pointer', fontFamily:FONTS.body, whiteSpace:'nowrap', flexShrink:0 }}>
                    {advancing===t.id?'…':sm.nextLabel}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}